import React from 'react';
import { UserDisplayName } from '@/shared/components/UserDisplayName';
import { getFinalDisplayName } from '@/shared/utils/displayName';

export function RequestCard({ 
  request, 
  onClick, 
  onVoteClick, 
  currentUser,
  isVoting,
  showVoteButton = true,
  showUserName = true
}) {
  if (!request) {
    console.error('RequestCard: request prop is required');
    return null;
  }

  const handleCardClick = (e) => {
    // Only prevent navigation if clicking directly on the vote button or its children
    const clickedElement = e.target;
    const voteButton = e.currentTarget.querySelector('[data-vote-button]');
    
    if (voteButton && (clickedElement === voteButton || voteButton.contains(clickedElement))) {
      console.log('RequestCard: Click on vote button, not navigating');
      return;
    }
    
    console.log('RequestCard clicked, navigating to request:', request.id);
    if (onClick) {
      onClick(request);
    }
  };

  const handleVoteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Vote button clicked for request:', request.id);
    if (onVoteClick) {
      onVoteClick(request.id);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getInterestColor = () => {
    const count = request.voteCount || 0;
    if (count === 0) return 'from-gray-400 to-gray-500';
    if (count < 10) return 'from-blue-400 to-blue-600';
    if (count < 50) return 'from-purple-400 to-purple-600';
    return 'from-green-400 to-green-600';
  };

  const wasUpdated = request.updatedAt && 
    new Date(request.updatedAt) > new Date(request.createdAt);

  const isOwner = currentUser && currentUser.dbUserId && request.userId === currentUser.dbUserId;

  // Get the display name to show
  const displayName = getFinalDisplayName(request.userEmail, request.userDisplayName);

  return (
    <div 
      className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group relative"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
      aria-label={`View details for ${request.title}`}
    >
      {/* Header with interest badge */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">
              {request.title}
            </h3>
            {showUserName && (
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <UserDisplayName 
                    displayName={displayName}
                    className="text-gray-700 font-medium"
                  />
                </span>
                {isOwner && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Your Request
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <div className={`bg-gradient-to-r ${getInterestColor()} text-white px-4 py-2 rounded-full font-bold shadow-md text-center min-w-[80px]`}>
              {request.voteCount || 0} 👍
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-gray-700 leading-relaxed line-clamp-3 mb-4">
          {request.description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatDate(request.createdAt)}
            </span>
            {wasUpdated && (
              <span className="flex items-center text-purple-600">
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Updated
              </span>
            )}
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {request.commentCount || 0} comments
            </span>
          </div>

          {showVoteButton && currentUser && (
            <button
              data-vote-button
              onClick={handleVoteClick}
              disabled={isVoting}
              className={`px-4 py-2 rounded-lg font-semibold cursor-pointer transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 flex items-center text-sm z-10 relative ${
                request.userHasVoted
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
              } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={request.userHasVoted ? 'Remove vote' : 'Vote for this request'}
            >
              {isVoting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {request.userHasVoted ? 'Removing...' : 'Adding...'}
                </>
              ) : request.userHasVoted ? (
                <>
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Supporting
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  I'm Interested
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Hover overlay for better visual feedback */}
      <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none"></div>
    </div>
  );
}